import type { Rec, Finding } from "@shared/types.js";
import { fnum } from "../lib/format.js";
import { RULE_TITLE, RULE_DESC, sevPill } from "../lib/rules.js";

const RULES_NEW = [
  "priced-without-cr", "received-not-priced", "double-entry", "date-without-status",
  "missing-input-date", "entered-not-crosschecked", "duplicate-record",
];
const RULES_EST = [
  "estimated-though-received", "double-entry", "date-without-status", "missing-input-date",
  "crosscheck-without-entry", "duplicate-record", "missing-category",
];

/** Inline rule roll-up scoped to the records shown in a section. */
export function CrosscheckSummary({
  kind, rows, findings,
}: {
  kind: "new" | "est"; rows: Rec[]; findings: Finding[];
}) {
  const ids = new Set(rows.map((r) => r.uid));
  const rules = kind === "new" ? RULES_NEW : RULES_EST;
  const fs = findings.filter((f) => ids.has(f.uid) && rules.includes(f.rule));

  const byRule = new Map<string, Finding[]>();
  for (const f of fs) {
    const list = byRule.get(f.rule);
    if (list) list.push(f); else byRule.set(f.rule, [f]);
  }
  const ordered = [...byRule.entries()].sort((a, b) => b[1].length - a[1].length);

  return (
    <div className="sec">
      <div className="card">
        <div className="card-head">
          <h3><i className="bi bi-shield-check hico" aria-hidden="true" />Crosscheck &mdash; {kind === "new" ? "New Rate" : "Estimation Rate"}</h3>
          <span className="sub">
            {fnum(fs.length)} finding{fs.length === 1 ? "" : "s"} on the records in scope
          </span>
        </div>
        {fs.length === 0 ? (
          <div className="empty">No mismatches found for the current filter.</div>
        ) : (
          <>
            <div className="tablewrap">
              <table>
                <thead>
                  <tr>
                    <th>Check</th><th>What it means</th>
                    <th className="n">Records</th><th>Severity</th>
                  </tr>
                </thead>
                <tbody>
                  {ordered.map(([rule, arr]) => {
                    const sev = arr[0]!.severity;
                    return (
                      <tr key={rule}>
                        <td className="strong">{RULE_TITLE[rule] ?? rule}</td>
                        <td>{RULE_DESC[rule] ?? arr[0]!.message}</td>
                        <td className="n">{fnum(arr.length)}</td>
                        <td><span className={`pill ${sevPill(sev)}`}>{sev}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="tblnote">
              Full row-level detail, filtering and export live in the <b>Crosscheck</b> tab.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
